<template>
  <!-- `embedded` renders this view as a Reports tab body: no shell, no page heading of its own. -->
  <component :is="embedded ? 'div' : AppShell">
    <section class="view on crawlers-view" :aria-labelledby="embedded ? undefined : 'crawlers-title'" :aria-label="embedded ? t('cw_h') : undefined">
      <div class="pagehead" :class="{ 'ph-embedded': embedded }">
        <div v-if="!embedded">
          <h1 id="crawlers-title">{{ t("cw_h") }}</h1>
          <p class="sub">{{ t("cw_sub") }}</p>
        </div>
        <button type="button" class="askmaya-chip" @click="askMaya"><img :src="mayaImage" alt="">{{ t("maya_launch") }}</button>
      </div>

      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="error" class="empty" role="alert">{{ error }} <button type="button" class="atbtn" @click="load">{{ t("account_retry") }}</button></p>
      <p v-else-if="!currentBrand" class="empty">{{ t("no_brands") }}</p>
      <template v-else-if="data">
        <template v-if="!data.connected">
          <div class="notebox setup-intro">{{ t("cw_setup_intro") }}</div>
          <SetupCards :site-token="data.site_token" @copy="copy" />
        </template>

        <template v-else>
          <p class="cifoot tiles-subtitle">{{ t("cw_tiles_sub") }}</p>
          <div class="cwtiles">
            <div v-for="tier in tiers" :key="tier" class="cwtile">
              <div class="ct">{{ tierLabel(tier) }}</div>
              <div class="cn2">{{ number(data.tiers?.[tier]?.hits) }}</div>
              <div class="cs">{{ number(data.tiers?.[tier]?.verified) }} {{ t("cw_verified") }} · {{ number(unverified(tier)) }} {{ t("cw_unverified") }}</div>
            </div>
          </div>

          <section class="inscard" aria-labelledby="crawler-trend-title">
            <h2 id="crawler-trend-title">{{ t("cw_ts_h") }}</h2>
            <svg class="crawler-chart" viewBox="0 0 560 170" role="img" :aria-label="t('cw_ts_h')">
              <line v-for="tick in [0, 1, 2, 3, 4]" :key="tick" x1="10" x2="550" :y1="12 + tick * 36" :y2="12 + tick * 36" />
              <polyline :points="trendPoints('hits')" stroke="#2847C4" />
              <polyline :points="trendPoints('verified')" stroke="#0E8A6D" />
            </svg>
            <ul class="legend" :aria-label="t('cw_ts_h')">
              <li><i class="all" aria-hidden="true"></i>{{ t("cw_legend_all") }}</li>
              <li><i class="verified" aria-hidden="true"></i>{{ t("cw_legend_ver") }}</li>
              <li class="date-range">{{ dateRange }}</li>
            </ul>
          </section>

          <section class="inscard" aria-labelledby="crawler-bots-title">
            <h2 id="crawler-bots-title">{{ t("cw_bots_h") }}</h2>
            <div v-if="data.bots?.length" class="table-wrap">
              <table class="rtable">
                <thead><tr><th scope="col">{{ t("cw_col_bot") }}</th><th scope="col">{{ t("cw_col_tier") }}</th><th scope="col">{{ t("cw_col_hits") }}</th><th scope="col">{{ t("cw_col_ver") }}</th><th scope="col">{{ t("cw_col_last") }}</th></tr></thead>
                <tbody><tr v-for="bot in data.bots" :key="`${bot.bot}-${bot.tier}`"><th scope="row">{{ bot.bot }}</th><td>{{ tierLabel(bot.tier) }}</td><td class="mono"><strong>{{ number(bot.hits) }}</strong></td><td class="mono">{{ verifiedPercent(bot) }}%</td><td class="last-seen"><time v-if="bot.last_seen" :datetime="isoDate(bot.last_seen)">{{ relativeTime(bot.last_seen) }}</time><span v-else>—</span></td></tr></tbody>
              </table>
            </div>
            <p v-else class="empty">{{ t("cw_none_bots") }}</p>
          </section>

          <section class="inscard" aria-labelledby="crawler-citations-title">
            <h2 id="crawler-citations-title">{{ t("cw_cnc_h") }}</h2>
            <div class="notebox citation-summary">{{ format("cw_cnc_reads", data.pages || 0) }} <template v-if="data.cited?.has_run">{{ data.cited.our_cited ? format("cw_cnc_cited", Math.round(data.cited.rec_pct || 0)) : t("cw_cnc_absent") }}</template></div>
            <template v-if="data.top_paths?.length">
              <p class="csub paths-title">{{ t("cw_cnc_paths") }}</p>
              <ol class="path-list">
                <li v-for="path in data.top_paths" :key="path.path">
                  <div class="path-main"><strong class="mono">{{ path.path }}</strong><span>{{ t("cw_col_by") }}: {{ path.bots?.join(", ") || "—" }} · {{ number(path.verified) }} {{ t("cw_verified") }}</span></div>
                  <div class="path-hits"><strong>{{ number(path.hits) }}</strong><span>{{ t("cw_col_hits").toLowerCase() }}</span></div>
                </li>
              </ol>
            </template>
          </section>

          <section class="inscard" aria-labelledby="crawler-referrals-title">
            <h2 id="crawler-referrals-title">{{ t("cw_ref_h") }}</h2>
            <div v-if="data.referrals?.length" class="referrals">
              <div v-for="referral in data.referrals" :key="referral.source" class="barrow"><span class="bl">{{ referral.source }}</span><span class="bt" aria-hidden="true"><span class="bf" :style="{ width: referralWidth(referral.n) }"></span></span><span class="bv">{{ number(referral.n) }} <small>{{ t("cw_visits") }}</small></span></div>
            </div>
            <p v-else class="referral-empty">{{ t("cw_ref_none") }}</p>
          </section>

          <section class="inscard" aria-labelledby="crawler-setup-title">
            <h2 id="crawler-setup-title">{{ t("cw_setup_h") }}</h2>
            <TokenRow :site-token="data.site_token" @copy="copy" />
            <p class="hint setup-more">{{ t("cw_setup_more") }}</p>
          </section>
        </template>
      </template>
      <p class="sr-only" role="status" aria-live="polite">{{ copyStatus }}</p>
    </section>
  </component>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { getCrawlerSummary } from "../api/crawlerSummary";
import { assetUrl } from "../api/client";
import AppShell from "../layout/AppShell.vue";
import { currentBrand } from "../stores/brands";
import { useI18n } from "../useI18n";

defineProps({ embedded: { type: Boolean, default: false } });

const emitCopy = { emits: ["copy"] };
const TokenRow = defineComponent({
  ...emitCopy,
  props: { siteToken: { type: String, default: "" } },
  setup(props, { emit }) {
    return () => h("div", { class: "cwtok" }, [
      h("span", { class: "cl" }, t("cw_tok")),
      h("code", props.siteToken),
      h("button", { type: "button", class: "albtn", onClick: () => emit("copy", props.siteToken) }, copiedValue.value === props.siteToken ? t("ob_copied") : t("ob_copy")),
    ]);
  },
});
const SetupCards = defineComponent({
  ...emitCopy,
  props: { siteToken: { type: String, default: "" } },
  setup(props, { emit }) {
    return () => h("div", { class: "setup-cards" }, [
      h("section", { class: "inscard", "aria-labelledby": "crawler-wordpress-title" }, [h("h2", { id: "crawler-wordpress-title" }, t("cw_wp_h")), h("p", { class: "csub" }, t("cw_wp_txt")), h(TokenRow, { siteToken: props.siteToken, onCopy: (value) => emit("copy", value) }), h("a", { class: "btn download", href: assetUrl(`/assets/download/${globalThis.SOA_CONFIG?.insightsArchive || "soarank-insights.zip"}`) }, t("cw_wp_btn"))]),
      h("section", { class: "inscard", "aria-labelledby": "crawler-beacon-title" }, [h("h2", { id: "crawler-beacon-title" }, t("cw_js_h")), h("p", { class: "csub" }, t("cw_js_txt")), h("div", { class: "cwcode" }, [h("code", { class: "cwsnip" }, snippet.value), h("button", { type: "button", class: "cwcopy", onClick: () => emit("copy", snippet.value) }, copiedValue.value === snippet.value ? t("ob_copied") : t("ob_copy"))]), h("p", { class: "hint" }, t("cw_js_note"))]),
      h("section", { class: "inscard", "aria-labelledby": "crawler-cloudflare-title" }, [h("h2", { id: "crawler-cloudflare-title" }, t("cw_cf_h")), h("p", { class: "csub" }, t("cw_cf_txt")), h("a", { href: "https://developers.cloudflare.com/ai-crawl-control/", target: "_blank", rel: "noopener" }, t("cw_cf_link"))]),
    ]);
  },
});

const router = useRouter();
const { t } = useI18n();
const mayaImage = assetUrl("/assets/img/maya-256.webp");
const data = ref(null), loading = ref(true), error = ref(""), copiedValue = ref(""), copyStatus = ref("");
const tiers = ["retrieval", "index", "training"];
let controller;
let copyTimer;
const snippet = computed(() => `<script src="${window.location.origin}${assetUrl("/assets/js/beacon.js")}?t=${data.value?.site_token || ""}" async></scr${"ipt"}>`);
const dateRange = computed(() => { const days = data.value?.days || []; return days.length ? `${days[0].day} → ${days[days.length - 1].day}` : ""; });
const referralMax = computed(() => Math.max(1, ...(data.value?.referrals || []).map((item) => Number(item.n) || 0)));

async function load() {
  controller?.abort();
  controller = new AbortController();
  data.value = null; error.value = ""; loading.value = true;
  try { if (currentBrand.value) data.value = await getCrawlerSummary(currentBrand.value.id, { signal: controller.signal }); }
  catch (caught) { if (caught.name !== "AbortError") error.value = caught.message || t("cw_err"); }
  finally { loading.value = false; }
}
function askMaya() { router.push({ path: "/maya", query: { prompt: t("maya_sk_cw_q") } }); }
function tierLabel(tier) { return t(`cw_tier_${tier}`); }
function number(value) { return (Number(value) || 0).toLocaleString(); }
function unverified(tier) { const row = data.value?.tiers?.[tier] || {}; return (Number(row.hits) || 0) - (Number(row.verified) || 0); }
function verifiedPercent(bot) { return bot.hits ? Math.round(Number(bot.verified) / Number(bot.hits) * 100) : 0; }
function format(key, value) { return t(key).replace("{b}", String(value)); }
function trendPoints(key) { const days = data.value?.days || [], max = Math.max(4, ...days.map((day) => Number(day.hits) || 0)); return days.map((day, index) => `${10 + index * 540 / Math.max(1, days.length - 1)},${156 - (Number(day[key]) || 0) / max * 144}`).join(" "); }
function referralWidth(value) { return `${Math.max(2, (Number(value) || 0) / referralMax.value * 100).toFixed(1)}%`; }
function isoDate(value) { return new Date(Number(value) * 1000).toISOString(); }
function relativeTime(value) {
  const seconds = Math.max(0, Math.round(Date.now() / 1000 - Number(value)));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
async function copy(value) {
  if (!value) return;
  try {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(value);
    } catch {
      const input = document.createElement("textarea");
      input.value = value; input.setAttribute("readonly", ""); input.style.position = "fixed"; input.style.opacity = "0";
      document.body.append(input); input.select(); document.execCommand("copy"); input.remove();
    }
    copiedValue.value = value; copyStatus.value = t("ob_copied");
    clearTimeout(copyTimer); copyTimer = setTimeout(() => { copiedValue.value = ""; copyStatus.value = ""; }, 1500);
  } catch { copyStatus.value = "Unable to copy. Select and copy the value manually."; }
}
onMounted(load);
watch(() => currentBrand.value?.id, (next, previous) => { if (next && next !== previous) load(); });
onBeforeUnmount(() => { controller?.abort(); clearTimeout(copyTimer); });
</script>

<style scoped>
.crawlers-view { max-width: 1040px; }
.ph-embedded { justify-content: flex-end; margin-bottom: 0; }
.askmaya-chip { display: inline-flex; align-items: center; gap: 7px; padding: 7px 15px; border: 1px solid var(--line); border-radius: 100px; background: var(--card); color: var(--ink); font-family: inherit; font-size: var(--fs-footnote); font-weight: 600; cursor: pointer; }
.askmaya-chip:hover { border-color: var(--indigo); }
.askmaya-chip:focus-visible, .cwcopy:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }
.askmaya-chip img { width: 20px; height: 20px; border-radius: 50%; }
.setup-intro, .tiles-subtitle { margin-bottom: 16px; }
.setup-cards { display: grid; gap: 18px; }
.inscard { margin-bottom: 18px; padding: 20px; border: 1px solid var(--line); background: var(--bg); }
.inscard h2 { margin: 0 0 18px; font-size: 1rem; }
.inscard > h2 + .csub { margin-top: 0; }
.inscard .csub { margin: 0 0 16px; }
:deep(.setup-cards .inscard h2) { margin-bottom: 18px; }
:deep(.setup-cards .inscard .csub) { margin: 0 0 16px; }
.download { display: inline-block; text-decoration: none; }
.crawler-chart { display: block; width: 100%; height: auto; max-height: 280px; }
.crawler-chart line { stroke: var(--line); stroke-width: 1; }
.crawler-chart polyline { fill: none; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.legend { display: flex; flex-wrap: wrap; gap: 10px 20px; margin: 12px 0 0; padding: 0; list-style: none; color: var(--body); font-size: var(--fs-caption); }
.legend li { display: flex; align-items: center; gap: 7px; }
.legend i { width: 9px; height: 9px; }
.legend i.all { background: #2847C4; }
.legend i.verified { background: #0E8A6D; }
.legend .date-range { color: var(--mute); }
.table-wrap { overflow-x: auto; }
.rtable th[scope="row"] { color: var(--ink); font-weight: 600; text-align: left; white-space: nowrap; }
.last-seen { color: var(--mute); white-space: nowrap; }
.citation-summary { margin-bottom: 12px; }
.paths-title { margin: 0 0 4px; }
.path-list { margin: 0; padding: 0; list-style: none; }
.path-list li { display: flex; align-items: center; gap: 18px; padding: 13px 0; border-bottom: 1px solid var(--line); }
.path-main { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 4px; }
.path-main strong { overflow-wrap: anywhere; font-size: .8125rem; }
.path-main span, .path-hits span { color: var(--mute); font-size: var(--fs-caption); }
.path-hits { display: flex; flex: none; flex-direction: column; text-align: right; }
.referrals { display: grid; gap: 12px; }
.barrow { display: grid; grid-template-columns: minmax(90px, 160px) 1fr minmax(70px, auto); align-items: center; gap: 12px; }
.bl { overflow-wrap: anywhere; }
.bt { height: 8px; overflow: hidden; background: var(--tint); }
.bf { display: block; height: 100%; background: var(--indigo); }
.bv { text-align: right; font-variant-numeric: tabular-nums; }
.bv small, .referral-empty { color: var(--mute); }
.referral-empty { margin: 0; font-size: var(--fs-footnote); }
.setup-more { margin-top: 4px; }
@media (max-width: 640px) { .pagehead { align-items: flex-start; gap: 14px; } .askmaya-chip { flex: none; } .inscard { padding: 16px; } .barrow { grid-template-columns: minmax(72px, 105px) 1fr; } .barrow .bv { grid-column: 2; } .path-list li { align-items: flex-start; } }
</style>
